import {
	type Dtos,
	type FredSyncRunId,
	FredSyncRunStatus,
	type USStateOrTerritory,
	usStateOrTerritorySchema,
} from "@inrev/common";
import {
	useFetchFredLatestSyncRun,
	useFetchFredSnapshot,
	useFetchFredSyncRun,
	useRefreshFred,
} from "@portal/api/fred/admin";
import { ReportedRetryableLoadError } from "@portal/components/features/load-errors";
import { DropdownInput } from "@portal/components/form/inputs/DropdownInput";
import { EmptyState, InProgressBanner, TrendMetricCard } from "@portal/components/patterns";
import {
	Banner,
	Button,
	Icon,
	SegmentedControls,
	Spinner,
	Text,
} from "@portal/components/primitives";
import { useCallback, useId, useMemo, useState } from "react";
import { Tooltip as ReactTooltip } from "react-tooltip";
import { FredTrendChart } from "./FredTrendChart";
import { MarketAnalyticsSection } from "./MarketAnalyticsSection";

type SnapshotCard = Dtos.MarketIntelligence.Fred.Snapshot.Card;
type ChangePeriod = Dtos.MarketIntelligence.Fred.Snapshot.ChangePeriod;

/** Must be non-empty because `DropdownInput` reserves `""` for its placeholder. */
const NATIONAL_OPTION_VALUE = "NATIONAL";

const MIN_SPARKLINE_POINTS = 2;

const CHANGE_PERIOD_LABEL: Record<ChangePeriod, string> = {
	mom: "last month",
	qoq: "last quarter",
	yoy: "last year",
};

const unsignedChangeDisplay = (display: string): string => {
	const sign = display.charAt(0);
	return sign === "+" || sign === "-" ? display.slice(1) : display;
};

/** These series use incompatible scales, so each requires its own chart axis. */
const TREND_SERIES = [
	{ seriesCode: "DGS10", variant: "line" as const },
	{ seriesCode: "TTLCONS", variant: "line" as const },
	{ seriesCode: "UNRATE", variant: "line" as const },
	/** Annual shares are discrete observations, not a continuous line. */
	{ seriesCode: "SBCSS10B0V0FRB", variant: "column" as const },
] as const;

/** Annual dollar magnitudes use columns; continuous rates and employment use lines. */
const STATE_TREND_SERIES = [
	{ metricFamily: "construction_gdp", variant: "column" as const },
	{ metricFamily: "construction_employment", variant: "line" as const },
	{ metricFamily: "unemployment", variant: "line" as const },
	{ metricFamily: "real_gdp", variant: "column" as const },
] as const;

const monthYear = (isoDate: string) =>
	new Date(`${isoDate}T00:00:00Z`).toLocaleDateString("en-US", {
		month: "short",
		year: "numeric",
		timeZone: "UTC",
	});

const formatRefreshedAt = (value: { toString(): string }) =>
	new Date(value.toString()).toLocaleString("en-US", {
		month: "short",
		day: "numeric",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	});

/** Interrupted runs may still read `running`; never present their start time as a successful refresh. */
const lastRefreshedLabel = (
	run: Dtos.MarketIntelligence.Fred.SyncRun.Response | null | undefined,
	isRefreshing: boolean,
): string | null => {
	if (isRefreshing) return "Refreshing…";
	if (run === undefined) return null;
	if (run === null) return "Never refreshed";
	if (run.isStale) return "Last refresh was interrupted";
	if (run.status === FredSyncRunStatus.Running) return "Refreshing…";
	if (run.completedAt === null) return "Never refreshed";
	return `Last refreshed ${formatRefreshedAt(run.completedAt)}`;
};

type SyncRun = Dtos.MarketIntelligence.Fred.SyncRun.Response;

const refreshProgressLabel = (run: SyncRun | undefined): string => {
	if (run === undefined) return "Starting refresh…";

	const done = run.seriesSucceeded + run.seriesSkipped + run.seriesUnavailable + run.seriesFailed;
	if (run.seriesAttempted === 0 || done === 0) {
		return "Refreshing economic indicators. This can take a few minutes.";
	}
	return `Refreshing economic indicators — ${done} of ${run.seriesAttempted} series. This can take a few minutes.`;
};

/** Prefer the most actionable failure, including partial runs that otherwise resemble success. */
const refreshProblemLabel = (
	run: SyncRun | undefined,
	refreshRejected: boolean,
	snapshotError: Error | null,
): string | null => {
	if (refreshRejected) return "Could not start a refresh. A refresh may already be running.";
	if (run !== undefined && run.isStale) {
		return "The last refresh was interrupted before it finished. Some indicators may be out of date.";
	}
	if (run?.status === FredSyncRunStatus.Failed) {
		return run.errorMessage ?? "The last refresh failed.";
	}
	if (run?.status === FredSyncRunStatus.Partial) {
		const failed = run.failures
			.slice(0, 3)
			.map((failure) => failure.seriesCode)
			.join(", ");
		const rest = run.failures.length > 3 ? `, and ${run.failures.length - 3} more` : "";
		return `The last refresh finished with ${run.seriesFailed} of ${run.seriesAttempted} series failing (${failed}${rest}).`;
	}
	if (snapshotError !== null) return "Showing the last loaded snapshot. Refreshing failed.";
	return null;
};

const freshnessLabel = (card: SnapshotCard): string => {
	if (card.latestDate === null) return "No data";
	return `As of ${monthYear(card.latestDate)}`;
};

const FredCard = ({ card, changePeriod }: { card: SnapshotCard; changePeriod: ChangePeriod }) => {
	const tooltipId = useId();
	const sparkline = useMemo(
		() =>
			card.sparkline.length >= MIN_SPARKLINE_POINTS
				? card.sparkline.map((point) => point.value)
				: undefined,
		[card.sparkline],
	);
	const tooltip = `${card.description} Frequency: ${card.frequencyLabel}. ${card.seasonalAdjustmentLabel}.`;

	return (
		<div className="flex flex-col gap-1" data-testid={`fred-card-${card.seriesCode}`}>
			<TrendMetricCard
				testId={`fred-metric-${card.seriesCode}`}
				label={card.label}
				value={card.display}
				valueTooltip={card.displayFull === card.display ? undefined : card.displayFull}
				series={sparkline}
				tone="accent"
				action={
					<div
						role="note"
						tabIndex={0}
						aria-label={`About ${card.label}`}
						data-tooltip-id={tooltipId}
						data-tooltip-content={tooltip}
					>
						<Icon type="information-circle" size="mini" color="tertiary" />
					</div>
				}
				trend={
					card.changeDisplay == null || card.changeDirection == null
						? null
						: {
								display: unsignedChangeDisplay(card.changeDisplay),
								direction: card.changeDirection,
							}
				}
				caption={CHANGE_PERIOD_LABEL[changePeriod]}
				footer={
					/* Units remain visible even when the card also displays a change. */
					<div className="flex items-baseline justify-between gap-2">
						<Text type="caption" color="tertiary">
							{`${card.frequencyLabel}, ${card.scaleLabel}`}
						</Text>
						<Text type="caption" color="tertiary">
							{freshnessLabel(card)}
						</Text>
					</div>
				}
			/>
			<ReactTooltip id={tooltipId} className="max-w-[320px]" style={{ zIndex: 9999 }} />
		</div>
	);
};

const CardGrid = ({
	cards,
	changePeriod,
}: {
	cards: readonly SnapshotCard[];
	changePeriod: ChangePeriod;
}) => (
	<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
		{cards.map((card) => (
			<FredCard key={card.seriesCode} card={card} changePeriod={changePeriod} />
		))}
	</div>
);

export const FredSnapshotTab = () => {
	const [stateCode, setStateCode] = useState<USStateOrTerritory | undefined>(undefined);
	const [changePeriod, setChangePeriod] = useState<ChangePeriod>("yoy");
	const [watchedRunId, setWatchedRunId] = useState<FredSyncRunId | undefined>(undefined);
	const snapshot = useFetchFredSnapshot(stateCode, changePeriod);
	const latestSyncRun = useFetchFredLatestSyncRun();
	const {
		mutate: refreshFred,
		isPending: isStartingRefresh,
		isError: refreshRejected,
	} = useRefreshFred();

	// A resolved mutation means accepted; the watched run supplies progress and the final outcome.
	const watchedRun = useFetchFredSyncRun(watchedRunId);
	const isRunning =
		watchedRun.data !== undefined &&
		watchedRun.data.status === FredSyncRunStatus.Running &&
		!watchedRun.data.isStale;
	const isRefreshing = isStartingRefresh || isRunning;

	const onRefresh = useCallback(() => {
		refreshFred(undefined, {
			onSuccess: (accepted) => setWatchedRunId(accepted.runId),
		});
	}, [refreshFred]);

	const reportedRun = watchedRun.data ?? latestSyncRun.data ?? undefined;
	const refreshedLabel = lastRefreshedLabel(watchedRun.data ?? latestSyncRun.data, isRefreshing);
	const refreshProblem = isRefreshing
		? null
		: refreshProblemLabel(reportedRun, refreshRejected, snapshot.error);

	const stateOptions = useMemo(
		() => [
			{ value: NATIONAL_OPTION_VALUE, label: "National only" },
			...(snapshot.data?.availableStateCodes ?? []).map((code) => ({
				value: code,
				label: code,
			})),
		],
		[snapshot.data?.availableStateCodes],
	);

	const showNationalOnly = useCallback(() => setStateCode(undefined), []);

	/** Parse the widened string value; unknown options safely fall back to national. */
	const onStateChange = useCallback((value: string) => {
		const parsed = usStateOrTerritorySchema.safeParse(value);
		setStateCode(parsed.success ? parsed.data : undefined);
	}, []);

	if (snapshot.data === undefined && snapshot.error !== null) {
		return (
			<ReportedRetryableLoadError
				resource="economic indicators"
				error={snapshot.error}
				onRetry={snapshot.refetch}
				retrying={snapshot.isFetching}
			/>
		);
	}

	if (snapshot.data === undefined) {
		return (
			<div className="flex h-full items-center justify-center">
				<Spinner />
			</div>
		);
	}

	const { groups, stateGroup, changePeriod: displayedChangePeriod } = snapshot.data;

	return (
		<div className="flex h-full flex-col gap-5 overflow-auto pb-2">
			<div className="flex shrink-0 flex-wrap items-end justify-between gap-4">
				<div className="grid w-fit grid-cols-[auto_200px] items-start gap-x-4 gap-y-1">
					<Text type="caption" color="tertiary">
						Change
					</Text>
					<Text type="caption" color="tertiary">
						State
					</Text>
					<SegmentedControls<ChangePeriod>
						value={changePeriod}
						onChange={setChangePeriod}
						aria-label="Change period"
					>
						<SegmentedControls.Segment value="mom">MoM</SegmentedControls.Segment>
						<SegmentedControls.Segment value="qoq">QoQ</SegmentedControls.Segment>
						<SegmentedControls.Segment value="yoy">YoY</SegmentedControls.Segment>
					</SegmentedControls>
					<DropdownInput
						value={stateCode ?? NATIONAL_OPTION_VALUE}
						options={stateOptions}
						onChange={onStateChange}
						testId="fred-state-select"
						compact
					/>
				</div>
				<div className="ml-auto flex flex-wrap items-center gap-3">
					{refreshedLabel !== null && (
						<Text type="caption" color="tertiary" data-testid="fred-last-refreshed">
							{refreshedLabel}
						</Text>
					)}
					<Button
						variant="outline"
						color="secondary"
						size="sm"
						leadingIcon={<Icon type="arrow-path" color="secondary" size="micro" />}
						loading={isRefreshing}
						onClick={onRefresh}
						data-testid="fred-refresh-button"
						trackingId="fred-snapshot-refresh"
					>
						Refresh
					</Button>
				</div>
			</div>

			{isRefreshing && (
				<InProgressBanner testId="fred-refresh-in-progress">
					<Text type="body">{refreshProgressLabel(watchedRun.data)}</Text>
				</InProgressBanner>
			)}

			{/* Prefer the specific refresh failure when the snapshot refetch fails with it. */}
			{refreshProblem !== null && (
				<Banner tone="danger" data-testid="fred-refresh-problem">
					<Banner.Leading>
						<Icon type="exclamation-circle" size="small" color="danger" />
					</Banner.Leading>
					<Banner.Content>
						<Text type="body" color="danger">
							{refreshProblem}
						</Text>
					</Banner.Content>
				</Banner>
			)}

			{groups.length === 0 && stateGroup === null ? (
				<EmptyState
					testId="fred-snapshot-empty-state"
					message="No economic indicators have been synced yet."
				/>
			) : (
				<>
					<MarketAnalyticsSection title="Trends" subtitle="National, last 10 years">
						<div className="grid grid-cols-1 gap-4 lg:grid-cols-2 2xl:grid-cols-4">
							{TREND_SERIES.map(({ seriesCode, variant }) => (
								<FredTrendChart key={seriesCode} seriesCode={seriesCode} variant={variant} />
							))}
						</div>
					</MarketAnalyticsSection>

					{/* Distinguish an empty selected state from the national-only view. */}
					{stateCode !== undefined && stateGroup === null && (
						<MarketAnalyticsSection
							title={`${stateCode} state indicators`}
							subtitle="State-specific economic conditions"
							testId="fred-state-section-empty"
						>
							<EmptyState
								testId="fred-state-empty-state"
								message={`No economic indicators have been synced for ${stateCode} yet.`}
								action={
									<Button
										variant="outline"
										color="secondary"
										size="sm"
										onClick={showNationalOnly}
										data-testid="fred-state-reset"
										trackingId="fred-snapshot-state-reset"
									>
										Show national only
									</Button>
								}
							/>
						</MarketAnalyticsSection>
					)}

					{stateGroup !== null && (
						<MarketAnalyticsSection
							title={`${stateGroup.stateCode} state indicators`}
							subtitle="State-specific economic conditions"
							testId="fred-state-section"
						>
							<div className="flex flex-col gap-4">
								<CardGrid cards={stateGroup.cards} changePeriod={displayedChangePeriod} />
								<div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
									{STATE_TREND_SERIES.flatMap(({ metricFamily, variant }) => {
										const card = stateGroup.cards.find((c) => c.metricFamily === metricFamily);
										return card === undefined
											? []
											: [
													<FredTrendChart
														key={card.seriesCode}
														seriesCode={card.seriesCode}
														title={`${stateGroup.stateCode} ${card.label}`}
														variant={variant}
													/>,
												];
									})}
								</div>
							</div>
						</MarketAnalyticsSection>
					)}

					{groups.map((group) => (
						<MarketAnalyticsSection
							key={group.seriesGroup}
							title={group.groupLabel}
							subtitle="National"
							testId={`fred-group-${group.seriesGroup}`}
						>
							<CardGrid cards={group.cards} changePeriod={displayedChangePeriod} />
						</MarketAnalyticsSection>
					))}
				</>
			)}
		</div>
	);
};
